/**
 * WidgetCodeGenerator - Gera código de embed do widget de webchat
 * 
 * Exibe o snippet de código JavaScript para incorporar o widget
 * no site do cliente, com instruções de instalação.
 * 
 * @version 1.0.0
 */

import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Check, Copy, Code, FileCode2, HelpCircle } from 'lucide-react';
import { toast } from 'sonner';

interface WidgetCodeGeneratorProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  inboxId: string;
}

export function WidgetCodeGenerator({ open, onOpenChange, inboxId }: WidgetCodeGeneratorProps) {
  const [copied, setCopied] = useState<string | null>(null);

  // URL base do widget - CDN Vercel
  // Produção: https://app.uaizychat.com.br ou subdomínio dedicado
  const widgetBaseUrl = 'https://app.uaizychat.com.br';
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;

  // Código básico de embed
  const basicCode = `<!-- UaizyChat Widget -->
<script>
  (function(w, d, s, o, f, js, fjs) {
    w['UaizyChat'] = o;
    w[o] = w[o] || function() { (w[o].q = w[o].q || []).push(arguments) };
    js = d.createElement(s); js.async = 1; js.src = f;
    fjs = d.getElementsByTagName(s)[0]; fjs.parentNode.insertBefore(js, fjs);
  }(window, document, 'script', 'uaizychat', '${widgetBaseUrl}/widget.js'));
  
  uaizychat('init', {
    inboxId: '${inboxId}'
  });
</script>`;

  // Código com identificação de visitante
  const advancedCode = `<!-- UaizyChat Widget com Identificação -->
<script>
  (function(w, d, s, o, f, js, fjs) {
    w['UaizyChat'] = o;
    w[o] = w[o] || function() { (w[o].q = w[o].q || []).push(arguments) };
    js = d.createElement(s); js.async = 1; js.src = f;
    fjs = d.getElementsByTagName(s)[0]; fjs.parentNode.insertBefore(js, fjs);
  }(window, document, 'script', 'uaizychat', '${widgetBaseUrl}/widget.js'));
  
  uaizychat('init', {
    inboxId: '${inboxId}',
    // Opcional: Identifique o usuário logado
    visitor: {
      name: 'Nome do Usuário',      // Substitua pelos dados reais
      email: 'usuario@email.com',   // Substitua pelos dados reais
      phone: '+5511999999999',      // Substitua pelos dados reais
      metadata: {
        // Dados customizados
        plano: 'premium',
        cliente_id: '12345'
      }
    }
  });
</script>`;

  // Código GTM
  const gtmCode = `<!-- Para Google Tag Manager -->
<script>
  window.uaizychatSettings = {
    inboxId: '${inboxId}'
  };
</script>
<script async src="${widgetBaseUrl}/widget.js"></script>`;

  // Código React/Next.js
  const reactCode = `// Para React/Next.js
import { useEffect } from 'react';

declare global {
  interface Window {
    uaizychat: (...args: any[]) => void;
  }
}

export function UaizyChatWidget() {
  useEffect(() => {
    // Carrega o script
    const script = document.createElement('script');
    script.src = '${widgetBaseUrl}/widget.js';
    script.async = true;
    document.body.appendChild(script);

    script.onload = () => {
      window.uaizychat('init', {
        inboxId: '${inboxId}'
      });
    };

    return () => {
      document.body.removeChild(script);
    };
  }, []);

  return null;
}

// Uso no App:
// <UaizyChatWidget />`;

  /**
   * Copia código para clipboard
   */
  const copyToClipboard = async (code: string, type: string) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(type);
      toast.success('Código copiado!');
      setTimeout(() => setCopied(null), 2000);
    } catch (error) {
      toast.error('Erro ao copiar código');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Code className="h-5 w-5 text-primary" />
            Código de Instalação
          </DialogTitle>
          <DialogDescription>
            Copie e cole este código no seu site para ativar o widget de chat.
          </DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="basic" className="mt-4">
          <TabsList className="grid grid-cols-4 w-full">
            <TabsTrigger value="basic" className="text-xs sm:text-sm">
              Básico
            </TabsTrigger>
            <TabsTrigger value="advanced" className="text-xs sm:text-sm">
              Avançado
            </TabsTrigger>
            <TabsTrigger value="gtm" className="text-xs sm:text-sm">
              GTM
            </TabsTrigger>
            <TabsTrigger value="react" className="text-xs sm:text-sm">
              React
            </TabsTrigger>
          </TabsList>

          {/* Código Básico */}
          <TabsContent value="basic" className="mt-4 space-y-4">
            <div className="flex items-start gap-2 p-4 rounded-lg bg-muted/50 border">
              <HelpCircle className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="font-medium">Instalação básica</p>
                <p className="text-muted-foreground mt-1">
                  Cole este código antes do <code className="bg-muted px-1 rounded">&lt;/body&gt;</code> do seu site.
                  O widget aparecerá automaticamente no canto inferior da página.
                </p>
              </div>
            </div>

            <div className="relative">
              <pre className="bg-muted p-4 rounded-lg overflow-x-auto text-sm font-mono">
                <code>{basicCode}</code>
              </pre>
              <Button
                size="sm"
                variant="secondary"
                className="absolute top-2 right-2"
                onClick={() => copyToClipboard(basicCode, 'basic')}
              >
                {copied === 'basic' ? (
                  <Check className="h-4 w-4" />
                ) : (
                  <Copy className="h-4 w-4" />
                )}
              </Button>
            </div>
          </TabsContent>

          {/* Código Avançado */}
          <TabsContent value="advanced" className="mt-4 space-y-4">
            <div className="flex items-start gap-2 p-4 rounded-lg bg-muted/50 border">
              <HelpCircle className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="font-medium">Identificação de visitantes</p>
                <p className="text-muted-foreground mt-1">
                  Use esta versão se você tem usuários logados e quer identificá-los
                  automaticamente no chat. Substitua os valores pelos dados reais do usuário.
                </p>
              </div>
            </div>

            <div className="relative">
              <pre className="bg-muted p-4 rounded-lg overflow-x-auto text-sm font-mono">
                <code>{advancedCode}</code>
              </pre>
              <Button
                size="sm"
                variant="secondary"
                className="absolute top-2 right-2"
                onClick={() => copyToClipboard(advancedCode, 'advanced')}
              >
                {copied === 'advanced' ? (
                  <Check className="h-4 w-4" />
                ) : (
                  <Copy className="h-4 w-4" />
                )}
              </Button>
            </div>
          </TabsContent>

          {/* Código GTM */}
          <TabsContent value="gtm" className="mt-4 space-y-4">
            <div className="flex items-start gap-2 p-4 rounded-lg bg-muted/50 border">
              <HelpCircle className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="font-medium">Google Tag Manager</p>
                <p className="text-muted-foreground mt-1">
                  Se você usa GTM, adicione este código como uma tag HTML personalizada.
                  Configure para disparar em "Todas as páginas".
                </p>
              </div>
            </div>

            <div className="relative">
              <pre className="bg-muted p-4 rounded-lg overflow-x-auto text-sm font-mono">
                <code>{gtmCode}</code>
              </pre>
              <Button
                size="sm"
                variant="secondary"
                className="absolute top-2 right-2"
                onClick={() => copyToClipboard(gtmCode, 'gtm')}
              >
                {copied === 'gtm' ? (
                  <Check className="h-4 w-4" />
                ) : (
                  <Copy className="h-4 w-4" />
                )}
              </Button>
            </div>
          </TabsContent>

          {/* Código React */}
          <TabsContent value="react" className="mt-4 space-y-4">
            <div className="flex items-start gap-2 p-4 rounded-lg bg-muted/50 border">
              <FileCode2 className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="font-medium">React / Next.js</p>
                <p className="text-muted-foreground mt-1">
                  Componente para aplicações React. Importe e use no seu App.tsx
                  ou layout principal.
                </p>
              </div>
            </div>

            <div className="relative">
              <pre className="bg-muted p-4 rounded-lg overflow-x-auto text-sm font-mono">
                <code>{reactCode}</code>
              </pre>
              <Button
                size="sm"
                variant="secondary"
                className="absolute top-2 right-2"
                onClick={() => copyToClipboard(reactCode, 'react')}
              >
                {copied === 'react' ? (
                  <Check className="h-4 w-4" />
                ) : (
                  <Copy className="h-4 w-4" />
                )}
              </Button>
            </div>
          </TabsContent>
        </Tabs>

        {/* Informações adicionais */}
        <div className="mt-6 p-4 rounded-lg bg-primary/5 border border-primary/20">
          <h4 className="font-medium flex items-center gap-2">
            <Badge variant="secondary">ID da Inbox</Badge>
            <code className="text-sm font-mono bg-muted px-2 py-0.5 rounded">
              {inboxId}
            </code>
          </h4>
          <p className="text-sm text-muted-foreground mt-2">
            Este ID é único da sua inbox e identifica suas conversas.
            Não compartilhe publicamente se quiser manter suas configurações privadas.
          </p>
        </div>

        {/* Botões */}
        <div className="flex justify-end mt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Fechar
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
